import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
} from '@mui/material';
import {
  PieChart,
  Pie,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { styled } from '@mui/material/styles';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../services/api';
import config from '../config';
import CircularProgress from '@mui/material/CircularProgress';

const vendorName = (v) =>
  v ? [v.fname, v.lname].filter(Boolean).join(' ') : null;

const imageUrl = (img) =>
  img && img.startsWith('http')
    ? img
    : `${process.env.REACT_APP_API_URL || config.API_URL}/uploads/products/${img}`;

const CHART_COLORS = [
  '#2ED573', '#2196F3', '#FF9900', '#FF1474', '#9B59B6',
  '#00A6FF', '#F1C40F', '#1ABC9C', '#E67E22', '#E74C3C',
];

const StyledCard = ({ title, count, change, icon: Icon }) => (
  <Paper
    sx={{
      position: 'relative',
      background: 'linear-gradient(135deg, rgba(0, 71, 50, 0.2) 0%, rgba(0, 40, 28, 0.2) 100%)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(46, 213, 115, 0.15)',
      borderRadius: '16px',
      p: 2.5,
      height: '100%',
      overflow: 'hidden',
    }}
  >
    <Box sx={{
      position: 'relative',
      background: 'linear-gradient(135deg, rgba(0, 40, 28, 0.6) 0%, rgba(0, 30, 20, 0.6) 100%)',
      borderRadius: '12px',
      p: 2,
      mb: 2
    }}>
      <Box sx={{
        backgroundColor: 'rgba(46, 213, 115, 0.1)',
        borderRadius: '12px',
        p: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon sx={{ color: '#2ED573', fontSize: 24 }} />
      </Box>
      <Typography variant="h3" sx={{
        color: '#fff',
        fontSize: '28px',
        fontWeight: 600,
        letterSpacing: '-0.5px',
        mt: 2,
      }}>
        {count}
      </Typography>
    </Box>

    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" sx={{
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '14px'
      }}>
        {title}
      </Typography>
      <Typography sx={{
        color: 'rgba(46, 213, 115, 0.8)',
        fontSize: '12px',
        fontWeight: 500
      }}>
        {change}
      </Typography>
    </Box>
  </Paper>
);

const ChartPaper = styled(Paper)(() => ({
  background: 'linear-gradient(135deg, rgba(0, 71, 50, 0.2) 0%, rgba(0, 40, 28, 0.2) 100%)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(46, 213, 115, 0.15)',
  borderRadius: '16px',
  padding: '24px',
  height: '100%',
}));

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    const item = payload[0];
    return (
      <Box
        sx={{
          backgroundColor: '#1a2233',
          p: 1.5,
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 1,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="body2" sx={{ color: '#fff' }}>
          {item.payload.name || label}: {formatter ? formatter(item.value) : item.value}
        </Typography>
      </Box>
    );
  }
  return null;
};

const EmptyState = ({ text }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>{text}</Typography>
  </Box>
);

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    byCategory: [],
    recentProducts: [],
    lowStockProducts: []
  });
  const [loading, setLoading] = useState(true);
  const [chartMetric, setChartMetric] = useState('value');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await api.getDashboardStats();
        setDashboardData({
          stats: response.stats || {},
          byCategory: response.byCategory || [],
          recentProducts: response.recentProducts || [],
          lowStockProducts: response.lowStockProducts || []
        });
        setError('');
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Could not load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    // Refresh data every 5 minutes
    const interval = setInterval(fetchDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const { stats, byCategory, recentProducts, lowStockProducts } = dashboardData;

  const cards = [
    {
      title: 'Total Products',
      count: (stats.totalProducts || 0).toLocaleString(),
      change: `${stats.activeProducts || 0} active`,
      icon: InventoryIcon,
    },
    {
      title: 'Active Products',
      count: (stats.activeProducts || 0).toLocaleString(),
      change: `${stats.deactiveProducts || 0} inactive`,
      icon: CheckCircleOutlineIcon,
    },
    {
      title: 'Total Stores',
      count: (stats.totalStores || 0).toLocaleString(),
      change: 'Active stores',
      icon: StorefrontIcon,
    },
    {
      title: 'Inventory Value',
      count: `Rs. ${(stats.inventoryValue || 0).toLocaleString()}`,
      change: 'Price × quantity',
      icon: AccountBalanceWalletIcon,
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ color: '#fff' }}>
          Dashboard
        </Typography>
        {error && (
          <Typography sx={{ color: '#FF1474', fontSize: '14px' }}>{error}</Typography>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StyledCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={5}>
          <ChartPaper>
            <Typography variant="h6" sx={{ color: '#fff', fontSize: '18px', fontWeight: 600, mb: 2 }}>
              Products by Category
            </Typography>
            {byCategory.length === 0 ? (
              <EmptyState text="No products yet" />
            ) : (
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={byCategory}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="count"
                      nameKey="name"
                    >
                      {byCategory.map((entry, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
            {/* Legend */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {byCategory.slice(0, 6).map((entry, index) => (
                <Chip
                  key={index}
                  size="small"
                  label={`${entry.name} (${entry.count})`}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.8)',
                    '& .MuiChip-label': { display: 'flex', alignItems: 'center', gap: 0.5 },
                  }}
                  icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: CHART_COLORS[index % CHART_COLORS.length], ml: 1 }} />}
                />
              ))}
            </Box>
          </ChartPaper>
        </Grid>
        <Grid item xs={12} md={7}>
          <ChartPaper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                {chartMetric === 'value' ? 'Inventory Value' : 'Total Weight'} by Category
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '20px', p: 0.5 }}>
                {[
                  { key: 'value', label: 'Value' },
                  { key: 'weight', label: 'Weight' }
                ].map(option => (
                  <Button
                    key={option.key}
                    size="small"
                    onClick={() => setChartMetric(option.key)}
                    sx={{
                      px: 2,
                      py: 0.25,
                      minWidth: 'auto',
                      borderRadius: '16px',
                      fontSize: '12px',
                      textTransform: 'none',
                      color: chartMetric === option.key ? '#0A0D1C' : 'rgba(255,255,255,0.6)',
                      backgroundColor: chartMetric === option.key ? '#2ED573' : 'transparent',
                      '&:hover': {
                        backgroundColor: chartMetric === option.key ? '#2ED573' : 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Box>
            </Box>
            {byCategory.length === 0 ? (
              <EmptyState text="No products yet" />
            ) : (
              <Box sx={{ width: '100%', height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byCategory} margin={{ top: 10, right: 20, bottom: 40, left: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }}
                      angle={-30}
                      textAnchor="end"
                      interval={0}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12 }}
                      tickFormatter={(v) => chartMetric === 'weight' ? `${v}g` : v.toLocaleString()}
                    />
                    <Tooltip
                      content={<ChartTooltip formatter={(v) => chartMetric === 'weight' ? `${Number(v.toFixed ? v.toFixed(2) : v)} g` : `Rs. ${v.toLocaleString()}`} />}
                      cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                    />
                    <Bar dataKey={chartMetric} radius={[4, 4, 0, 0]} maxBarSize={50}>
                      {byCategory.map((entry, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </ChartPaper>
        </Grid>
      </Grid>

      {/* Tables Row */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <ChartPaper>
            <Typography variant="h6" sx={{ color: '#fff', fontSize: '18px', fontWeight: 600, mb: 2 }}>
              Recently Added Products
            </Typography>
            {recentProducts.length === 0 ? (
              <EmptyState text="No products yet" />
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Qty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentProducts.map((product) => (
                      <TableRow key={product._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {product.images && product.images.length > 0 ? (
                              <Avatar
                                variant="rounded"
                                src={imageUrl(product.images[0])}
                                sx={{ width: 36, height: 36 }}
                              />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 36, height: 36, backgroundColor: 'rgba(255,255,255,0.08)' }}>
                                <InventoryIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                              </Avatar>
                            )}
                            <Typography sx={{ color: '#fff', fontSize: '14px' }}>{product.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{product.sku || '—'}</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{product.category || '—'}</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          {vendorName(product.vendor) || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#2ED573' }}>
                          Rs. {(product.price || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          {product.quantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </ChartPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartPaper>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WarningAmberIcon sx={{ color: '#FF9900' }} />
              <Typography variant="h6" sx={{ color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                Low Stock
              </Typography>
            </Box>
            {lowStockProducts.length === 0 ? (
              <EmptyState text="All products are well stocked" />
            ) : (
              <Box>
                {Object.values(
                  lowStockProducts.reduce((groups, product) => {
                    const vendor = vendorName(product.vendor) || product.category || 'No vendor';
                    const key = `${vendor}|${product.name}`;
                    if (!groups[key]) {
                      groups[key] = { name: product.name, vendor, items: 0, totalQty: 0 };
                    }
                    groups[key].items += 1;
                    groups[key].totalQty += product.quantity || 0;
                    return groups;
                  }, {})
                )
                  .sort((a, b) => a.vendor.localeCompare(b.vendor))
                  .slice(0, 8)
                  .map((group, index, arr) => (
                  <Box
                    key={`${group.vendor}-${group.name}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.5,
                      borderBottom: index !== arr.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: '#fff', fontSize: '14px' }}>{group.name}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                        {group.vendor}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={group.items > 1 ? `${group.items} items · ${group.totalQty} left` : `${group.totalQty} left`}
                      sx={{
                        backgroundColor: group.totalQty === 0 ? 'rgba(255, 20, 116, 0.15)' : 'rgba(255, 153, 0, 0.15)',
                        color: group.totalQty === 0 ? '#FF1474' : '#FF9900',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </ChartPaper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
